/**
 * ParameterLogsAgGridHook
 *
 * Grid for /admin/parameters/logs. parameter_push_logs grows without bound
 * (every parameter/EMV/keys push to every terminal writes a row), so this
 * uses AG Grid's server-side row model instead of shipping the whole table
 * to the browser: the grid asks for one page at a time over the LiveView
 * socket, and the LiveView answers with only that page plus the true total
 * count. See PlatformWeb.ParameterManagementLive.Logs for the query side.
 */

import { mountServerSideGrid } from "../base/ServerSideAgGridHook";

const PAGE_SIZE = 50;

const STATUS_COLORS = {
  acknowledged: "#16A34A",
  sent: "#2563EB",
  pending: "#D97706",
  failed: "#DC2626",
};

const statusBadge = (value) => {
  if (!value) return "";
  const bg = STATUS_COLORS[value] || "#6B7280";
  const label = value.charAt(0).toUpperCase() + value.slice(1);
  return `<span style="background:${bg};color:#fff;padding:2px 8px;border-radius:9999px;
    font-size:11px;font-weight:600;white-space:nowrap;">${label}</span>`;
};

const titleize = (value) =>
  (value || "N/A").toString().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const ParameterLogsAgGridHook = {
  mounted() {
    if (!window.agGrid || !window.agGrid.createGrid) {
      console.error("ParameterLogsAgGridHook: agGrid not found on window");
      return;
    }

    const self = this;

    const actionsRenderer = (params) => {
      if (!params.data) return "";
      const { id, status } = params.data;
      const wrap = document.createElement("div");
      wrap.style.cssText = "display:flex;align-items:center;gap:10px;padding:2px 0;";

      const btn = (label, color, onClick) => {
        const el = document.createElement("button");
        el.textContent = label;
        el.style.cssText = `color:${color};font-size:12px;cursor:pointer;background:none;border:none;padding:0;white-space:nowrap;`;
        el.addEventListener("click", (e) => {
          e.stopPropagation();
          onClick();
        });
        return el;
      };

      wrap.appendChild(
        btn("View", "#2563EB", () => self.pushEvent("show_log_details", { log_id: String(id) }))
      );

      if (status === "failed") {
        wrap.appendChild(
          btn("Retry", "#16A34A", () => self.pushEvent("retry_push", { log_id: String(id) }))
        );
      }

      return wrap;
    };

    const columnDefs = [
      {
        headerName: "Device",
        field: "device_serial",
        flex: 1.2,
        minWidth: 160,
        cellRenderer: (params) => {
          if (!params.data) return "";
          const { device_vendor, device_model } = params.data;
          const subtitle =
            device_vendor || device_model
              ? `${device_vendor || ""} ${device_model || ""}`.trim()
              : "Unknown Device";
          return `<div>
            <div style="font-weight:500;color:var(--ag-data-color);">${params.value || ""}</div>
            <div style="font-size:12px;color:var(--ag-secondary-foreground-color);">${subtitle}</div>
          </div>`;
        },
      },
      {
        headerName: "Template",
        field: "template_name",
        flex: 1.2,
        minWidth: 160,
        cellRenderer: (params) => {
          if (!params.data) return "";
          if (!params.value) return '<span style="color:var(--ag-secondary-foreground-color);">No Template</span>';
          const desc = params.data.template_description;
          return `<div>
            <div style="font-weight:500;color:var(--ag-data-color);">${params.value}</div>
            ${desc ? `<div style="font-size:12px;color:var(--ag-secondary-foreground-color);">${desc}</div>` : ""}
          </div>`;
        },
      },
      {
        headerName: "Status",
        field: "status",
        width: 130,
        cellRenderer: (p) => statusBadge(p.value),
      },
      {
        headerName: "Type",
        field: "config_type",
        width: 150,
        cellRenderer: (params) => {
          if (!params.data) return "";
          return `<div>
            <div>${titleize(params.value)}</div>
            <div style="font-size:12px;color:var(--ag-secondary-foreground-color);">${titleize(params.data.push_type)}</div>
          </div>`;
        },
      },
      {
        headerName: "Created",
        field: "created_at",
        width: 170,
        cellRenderer: (params) => {
          if (!params.value) return "";
          const [date, time] = String(params.value).split("T");
          return `<div>
            <div>${date || params.value}</div>
            <div style="font-size:12px;color:var(--ag-secondary-foreground-color);">${(time || "").split(".")[0]}</div>
          </div>`;
        },
      },
      {
        headerName: "Actions",
        colId: "actions",
        pinned: "right",
        width: 130,
        sortable: false,
        filter: false,
        floatingFilter: false,
        resizable: false,
        cellRenderer: actionsRenderer,
      },
    ];

    this.gridApi = mountServerSideGrid(this, {
      columnDefs,
      rowsEvent: "parameter_logs:rows",
      refreshEvent: "parameter_logs:refresh",
      pageSize: PAGE_SIZE,
      gridOptions: {
        overlayNoRowsTemplate: `<span style="padding:20px;color:var(--ag-secondary-foreground-color);">${
          this.el.dataset.empty || "No parameter push logs found."
        }</span>`,
      },
    });
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default ParameterLogsAgGridHook;
